import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ['home', 'about', 'products', 'features', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      data[key] = value.toString();
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6a3qv6a728k8ctu31m0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(data).toString(),
      });

      if (response.ok) {
        alert('Message sent successfully!');
        form.reset();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/788e409b-6dc6-49a1-86a0-42bb310f0ceb.png" alt="DOMAR Logo" className="h-10 w-10 object-contain" />
            <span className={`text-2xl font-bold transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>DOMAR d.o.o.</span>
          </div>
          
          <div className="flex items-center gap-12">
            {['home', 'about', 'products', 'features', 'contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className={`text-base font-medium capitalize transition-all whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                } ${activeSection === item ? 'border-b-2 border-current pb-1' : ''}`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/f7c5637704d56aa6689e91449fc49b00.jpg" 
            alt="Hero Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-block px-6 py-2 rounded-full border border-white/30 backdrop-blur-sm mb-8">
            <span className="text-white text-sm font-medium">iOS Development Experts</span>
          </div>
          
          <h1 className="text-7xl font-bold text-white leading-tight mb-8">
            Modern Applications<br />
            For iOS Platform<br />
            Enhanced Security
          </h1>
          
          <p className="text-xl text-white/90 font-light max-w-2xl mx-auto leading-relaxed">
            A team of experts developing cutting-edge applications that enhance user experience and improve the security of mobile devices
          </p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">
              <span className="bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-transparent">Innovating</span> the Future of iOS Development
            </h2>
            
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              DOMAR d.o.o. is a dedicated team of professionals specializing in iOS application development. We combine technical expertise with creative innovation to deliver products that stand out in the competitive mobile marketplace.
            </p>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              Our commitment to excellence drives us to create applications that not only meet but exceed user expectations. From concept to deployment, we ensure every detail is crafted with precision and care.
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <div className="text-xs uppercase tracking-widest bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent mb-4 font-semibold">OUR PRODUCTS</div>
            <h2 className="text-6xl font-bold text-white">Powerful iOS Solutions</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-10">
            {/* VPN Card */}
            <div className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-16 border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/20 hover:-translate-y-2 cursor-pointer" data-product-shop>
              <div className="flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 mb-8">
                <i className="ri-shield-check-line text-4xl text-white"></i>
              </div>
              
              <h3 className="text-4xl font-bold text-white mb-6">VPN</h3>
              
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-purple-400 mt-1"></i>
                  <span className="text-base">Military-grade encryption for ultimate privacy</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-purple-400 mt-1"></i>
                  <span className="text-base">Lightning-fast connection speeds worldwide</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-purple-400 mt-1"></i>
                  <span className="text-base">No-logs policy ensuring complete anonymity</span>
                </li>
              </ul>
            </div>

            {/* Cleaner Card */}
            <div className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-16 border border-cyan-500/20 hover:border-cyan-500/40 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/20 hover:-translate-y-2 cursor-pointer" data-product-shop>
              <div className="flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-cyan-500 mb-8">
                <i className="ri-brush-line text-4xl text-white"></i>
              </div>
              
              <h3 className="text-4xl font-bold text-white mb-6">Cleaner</h3>
              
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-cyan-400 mt-1"></i>
                  <span className="text-base">Smart cleanup removes unnecessary files instantly</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-cyan-400 mt-1"></i>
                  <span className="text-base">Optimize device performance with one tap</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-cyan-400 mt-1"></i>
                  <span className="text-base">Free up storage space efficiently and safely</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-6xl font-bold text-gray-900 mb-6">
              Why Choose Our Apps <span className="inline-block">🚀</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Built with cutting-edge technology and designed for exceptional user experience
            </p>
          </div>
          
          <div className="grid grid-cols-3 gap-8 mb-20">
            <div className="group relative overflow-hidden rounded-2xl aspect-[3/5] cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20smartphone%20with%20security%20shield%20icon%20glowing%20on%20screen%2C%20dark%20blue%20purple%20gradient%20background%2C%20digital%20protection%20concept%2C%20high-tech%20mobile%20security%20visualization%2C%20professional%20product%20photography%20style&width=600&height=1000&seq=feature-security-img&orientation=portrait" 
                alt="Security Feature" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 transform transition-all duration-300">
                <h3 className="text-3xl font-bold text-white mb-3">Advanced Security</h3>
                <p className="text-base text-white/90 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Industry-leading encryption and security protocols protect your data at all times
                </p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-2xl aspect-[3/5] cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=sleek%20modern%20iOS%20interface%20with%20smooth%20animations%2C%20minimalist%20app%20design%20on%20iPhone%20screen%2C%20clean%20user%20interface%20elements%2C%20contemporary%20mobile%20UX%20design%2C%20professional%20tech%20photography%20with%20soft%20lighting&width=600&height=1000&seq=feature-interface-img&orientation=portrait" 
                alt="Interface Feature" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 transform transition-all duration-300">
                <h3 className="text-3xl font-bold text-white mb-3">Intuitive Design</h3>
                <p className="text-base text-white/90 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Beautiful interfaces that are easy to navigate and delightful to use every day
                </p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-2xl aspect-[3/5] cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=lightning%20bolt%20speed%20icon%20with%20motion%20blur%20effect%2C%20vibrant%20cyan%20green%20gradient%20background%2C%20fast%20performance%20visualization%2C%20dynamic%20energy%20concept%2C%20modern%20digital%20illustration%20style&width=600&height=1000&seq=feature-performance-img&orientation=portrait" 
                alt="Performance Feature" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 transform transition-all duration-300">
                <h3 className="text-3xl font-bold text-white mb-3">Peak Performance</h3>
                <p className="text-base text-white/90 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Optimized code ensures smooth operation and minimal battery consumption
                </p>
              </div>
            </div>
          </div>

          {/* Feature Details */}
          <div className="bg-white rounded-3xl shadow-lg p-16">
            <div className="grid grid-cols-5 gap-12 mb-16">
              <div className="col-span-2">
                <div className="text-sm uppercase tracking-wider text-gray-500 mb-2">WHAT WE OFFER</div>
                <h3 className="text-4xl font-bold text-gray-900 italic">Comprehensive Features</h3>
              </div>
              <div className="col-span-3 flex items-center">
                <p className="text-lg text-gray-600 leading-relaxed w-3/4">
                  Every feature is carefully crafted to provide maximum value and ensure your mobile experience is secure, efficient, and enjoyable.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-8">
              <div className="group cursor-pointer">
                <div className="w-full h-64 rounded-2xl overflow-hidden mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=person%20using%20smartphone%20in%20modern%20lifestyle%20setting%2C%20warm%20natural%20lighting%2C%20casual%20everyday%20mobile%20usage%2C%20contemporary%20lifestyle%20photography%2C%20authentic%20user%20experience%20moment&width=800&height=600&seq=detail-lifestyle-img&orientation=landscape" 
                    alt="User Experience" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="inline-block px-4 py-2 rounded-full bg-lime-100 mb-4">
                  <span className="text-xs uppercase font-bold text-lime-800">USER EXPERIENCE</span>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 leading-tight">Seamless Integration with iOS Ecosystem</h4>
              </div>

              <div className="group cursor-pointer">
                <div className="w-full h-64 rounded-2xl overflow-hidden mb-6 bg-gray-900 flex items-center justify-center">
                  <i className="ri-flashlight-line text-8xl text-lime-400"></i>
                </div>
                <div className="inline-block px-4 py-2 rounded-full bg-lime-100 mb-4">
                  <span className="text-xs uppercase font-bold text-lime-800">INNOVATION</span>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 leading-tight">Cutting-Edge Technology Implementation</h4>
              </div>

              <div className="group cursor-pointer">
                <div className="w-full h-64 rounded-2xl overflow-hidden mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=premium%20iOS%20app%20interface%20on%20iPhone%20device%2C%20cool%20blue%20tones%2C%20professional%20product%20photography%2C%20modern%20mobile%20application%20showcase%2C%20sleek%20technology%20presentation&width=800&height=600&seq=detail-product-img&orientation=landscape" 
                    alt="Quality" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="inline-block px-4 py-2 rounded-full bg-lime-100 mb-4">
                  <span className="text-xs uppercase font-bold text-lime-800">QUALITY</span>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 leading-tight">Rigorous Testing and Quality Assurance</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-4 gap-16 mb-32">
            {/* Brand Column */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/788e409b-6dc6-49a1-86a0-42bb310f0ceb.png" alt="DOMAR Logo" className="h-8 w-8 object-contain" />
                <span className="text-xl font-bold text-white">DOMAR d.o.o.</span>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">
                Developing innovative iOS applications that enhance user experience and mobile device security.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-6 font-semibold">QUICK LINKS</h4>
              <div className="space-y-4">
                {['About', 'Products', 'Features'].map((link) => (
                  <button
                    key={link}
                    onClick={() => scrollToSection(link.toLowerCase())}
                    className="block text-base text-white hover:text-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {link}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="col-span-2">
              <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-6 font-semibold">CONTACT INFORMATION</h4>
              <div className="space-y-3 text-white">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-lg mt-1 flex-shrink-0"></i>
                  <div>
                    <div className="text-sm font-medium">Address:</div>
                    <div className="text-sm text-gray-300">Ulica Hrvatske Republike 19c</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-building-line text-lg mt-1 flex-shrink-0"></i>
                  <div>
                    <div className="text-sm font-medium">City:</div>
                    <div className="text-sm text-gray-300">Osijek</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-mail-line text-lg mt-1 flex-shrink-0"></i>
                  <div>
                    <div className="text-sm font-medium">Zip:</div>
                    <div className="text-sm text-gray-300">31000</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-global-line text-lg mt-1 flex-shrink-0"></i>
                  <div>
                    <div className="text-sm font-medium">Country:</div>
                    <div className="text-sm text-gray-300">Croatia</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-mail-send-line text-lg mt-1 flex-shrink-0"></i>
                  <div>
                    <div className="text-sm font-medium">Email:</div>
                    <div className="text-sm text-gray-300">information@domardoo.com</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-phone-line text-lg mt-1 flex-shrink-0"></i>
                  <div>
                    <div className="text-sm font-medium">Number:</div>
                    <div className="text-sm text-gray-300">+1 6812496668</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 border border-white/20">
            <h3 className="text-3xl font-bold text-white mb-8">Get In Touch</h3>
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-white mb-2">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white mb-2">Message</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 resize-none text-sm"
                  placeholder="Your message (max 500 characters)"
                ></textarea>
              </div>
              <button
                type="submit"
                className="px-8 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>

        {/* Large Brand Typography */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none" style={{ height: '440px' }}>
          <div className="text-[280px] font-black text-amber-100/10 tracking-tighter leading-none" style={{ marginBottom: '-60px' }}>
            DOMAR
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-400">
            © 2025 DOMAR d.o.o. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Powered by Readdy</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
